class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.header = document.querySelector('.header-wrapper');
    if (this.header) this.header.preventHide = false;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.header) this.header.preventHide = true;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden-drawer');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden-drawer');
    if (this.header) this.header.preventHide = false;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      // The re-render replaces the overlay node, so its close handler has to be re-bound.
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // Deliberately NOT opening the drawer here: adding to cart must not interrupt the page.
      // The contents above are refreshed while the drawer is closed, so the cart icon shows the
      // new count and opening the drawer later is correct. Merchant decision — adding several
      // add-ons in a row on a package page is unusable if each one throws the drawer open.
      // Confirmation now comes from sections/cart-toast.liquid instead.
      // DO NOT re-add this.open().
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ---------------------------------------------------------------------------
 * SPECIAL-ORDER FEE — ORPHAN CLEANUP (site-wide)
 *
 * Lines added by snippets/special-order-addons.liquid to carry a Globo option
 * charge are stamped `_so_parent` with the variant id of the add-on they pay to
 * fit. If that add-on leaves the cart the fee is a charge for nothing, so it has
 * to go with it.
 *
 * The package-page snippet already does this, but only where it renders. Remove
 * the build from the cart page — or from the drawer while on any other page —
 * and the orphan would survive to checkout and bill the customer for fitting a
 * part they are no longer buying. This file loads site-wide (it defines the
 * drawer element used in the header), so putting it here closes that gap.
 *
 * REDUCE-ONLY, exactly like the snippet: it removes an orphan and pulls a fee
 * down when it outnumbers its parent. It never adds and never increases — a fee
 * a customer deletes stays deleted. Nothing here hides or disables a control;
 * the fee remains an ordinary removable line.
 *
 * On package pages the snippet owns this (it is already doing the same work
 * alongside the running total), so we stand down there to avoid two writers
 * issuing the same change at once.
 * ------------------------------------------------------------------------- */
(function () {
  var busy = false;
  var budget = 3;               // per page load, so a failing change cannot loop

  function plan(cart) {
    var items = (cart && cart.items) || [];

    // Every line that is NOT a fee, by variant id -> quantity.
    var parents = {};
    items.forEach(function (it) {
      if (!(it.properties || {})._so_parent) {
        parents[String(it.variant_id)] = it.quantity;
      }
    });

    var changes = [];
    items.forEach(function (fee) {
      var p = fee.properties || {};
      if (!p._so_parent) return;
      var parentQty = parents[String(p._so_parent)];
      if (parentQty === undefined) {
        changes.push({ id: fee.key, quantity: 0 });      // orphan: parent has gone
      } else if (fee.quantity > parentQty) {
        changes.push({ id: fee.key, quantity: parentQty });
      }
    });
    return changes;
  }

  function apply(changes) {
    if (!changes.length || busy || budget <= 0) return;
    budget--;
    busy = true;
    var url = (window.routes && window.routes.cart_change_url) || '/cart/change';

    // Sequential: concurrent writes to one cart race each other.
    changes.reduce(function (chain, c) {
      return chain.then(function () {
        return fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: c.id, quantity: c.quantity })
        });
      }).catch(function () {});
    }, Promise.resolve()).then(function () {
      busy = false;
      if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'so-orphan-cleanup' });
      } else {
        window.location.reload();
      }
    });
  }

  var pending = false;
  function sweep() {
    // The package-page snippet is the owner where it exists.
    if (typeof window.__soRefreshTotal === 'function') return;
    if (pending || busy) return;
    pending = true;
    setTimeout(function () {
      pending = false;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { apply(plan(cart)); })
        .catch(function () {});
    }, 80);
  }

  function start() {
    sweep();
    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, sweep);
    } else {
      // No pub/sub in this theme build: notice cart writes as they happen.
      var originalFetch = window.fetch;
      window.fetch = function () {
        var u = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
        var out = originalFetch.apply(this, arguments);
        if (u.indexOf('/cart/') > -1 && u.indexOf('/cart.js') === -1) {
          out.then(function () { setTimeout(sweep, 250); }, function () {});
        }
        return out;
      };
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();